/**
 * One turn of the Zoe thread — a user message and Zoe's response to it — and
 * how a stored conversation becomes turns again when it is resumed.
 *
 * Lives outside `Agents.tsx` so the mapping from stored messages is plain,
 * testable code rather than logic buried in a page component.
 */

import type { InvestigationResponse } from '../../types/investigator';
import type { ConversationMessage } from '../../types/zoeConversations';
import { createInitialThinkingModel, type ZoeThinkingModel } from './zoeThinkingModel';

export interface ZoeTurn {
  /** Stable across the turn's lifecycle — the React key. A live turn's is client-generated; a resumed turn's is its `turn_id`. */
  id: string;
  question: string;
  status: 'loading' | 'success' | 'error';
  /** Adaptive thinking-state model driven by SSE progress events. */
  thinkingModel: ZoeThinkingModel;
  /** Epoch ms when this turn's investigation started. */
  thinkingStartedAt: number;
  result?: InvestigationResponse;
  error?: string;
  /**
   * Set when this turn answered a clarification by picking an option. Lets
   * the thread render the message as the user's choice, and lets the card it
   * answered mark which option was picked.
   */
  answeredClarification?: { clarification_id: string; option_id: string };
  /**
   * T30 — the answer's text, for a resumed turn recorded before full
   * responses were kept. Rendered as text; there is no `result` to render.
   */
  archivedAnswer?: string;
  /**
   * T30 — the server's id for this turn, present on resumed turns. The cursor
   * for loading older messages.
   */
  turnId?: string;
  /** Set once the answer has finished typing on any surface, so a surface
   *  that mounts the turn later (the full page after the pop-up, or the
   *  pop-up reopened) shows it settled instead of replaying the typewriter.
   *  A resumed turn starts revealed: it was shown when it was first asked. */
  revealed?: boolean;
}

/**
 * A stored message, as a thread turn.
 *
 * Three shapes, in order of preference:
 *   - **recorded response** — the answer exactly as it was first shown; renders
 *     through the same components as a live answer.
 *   - **clarification, no response** — enough to redraw the question and its
 *     options (the card reads only `status` and `clarification`).
 *   - **text only** — an answer from before responses were kept.
 */
export function turnFromMessage(message: ConversationMessage): ZoeTurn {
  const base: ZoeTurn = {
    id: message.turn_id,
    turnId: message.turn_id,
    question: message.question,
    status: 'success',
    thinkingModel: createInitialThinkingModel(),
    thinkingStartedAt: Date.parse(message.asked_at) || 0,
    revealed: true,
    ...(message.answered_clarification
      ? {
          answeredClarification: {
            clarification_id: message.answered_clarification.clarification_id,
            option_id: message.answered_clarification.option_id,
          },
        }
      : {}),
  };

  if (message.response) return { ...base, result: message.response };

  if (message.clarification) {
    return {
      ...base,
      // Deliberately partial: the clarification card reads only these two
      // fields, and nothing else ever receives a clarification turn's result.
      result: {
        status: 'needs_clarification',
        clarification: message.clarification,
        conversation_id: undefined,
      } as unknown as InvestigationResponse,
    };
  }

  return { ...base, archivedAnswer: message.answer_text || message.headline };
}
